import React, { useEffect, useRef } from "react";
import "./MessageBox.css";

export type MessageBoxType =
  | "error"
  | "warning"
  | "info"
  | "success"
  | "confirm";
export type MessageBoxButton =
  | "ok"
  | "cancel"
  | "yes"
  | "no"
  | "agree"
  | "disagree"
  | "retry"
  | "close"
  | "upgrade";

export interface MessageBoxProps {
  /** Whether the message box is visible */
  show: boolean;
  /** Type of message box (affects styling and default icon) */
  type: MessageBoxType;
  /** Header title for the message box */
  title: string;
  /** Main message content */
  message: string;
  /** Optional detailed description */
  description?: string;
  /** Buttons to display (array of button types) */
  buttons: MessageBoxButton[];
  /** Callback when a button is clicked */
  onButtonClick: (button: MessageBoxButton) => void;
  /** Callback when the message box should be closed */
  onClose: () => void;
  /** Optional custom icon (overrides default type icon) */
  icon?: string;
  /** Whether to show the close button (X) in the top right */
  showCloseButton?: boolean;
  /** Custom button labels (overrides default labels) */
  buttonLabels?: Partial<Record<MessageBoxButton, string>>;
  /** Whether the message box can be closed by clicking outside */
  closeOnBackdrop?: boolean;
}

export const MessageBox: React.FC<MessageBoxProps> = ({
  show,
  type,
  title,
  message,
  description,
  buttons,
  onButtonClick,
  onClose,
  icon,
  showCloseButton = true,
  buttonLabels = {},
  closeOnBackdrop = true,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const firstButtonRef = useRef<HTMLButtonElement>(null);

  // Focus management
  useEffect(() => {
    if (show && overlayRef.current) {
      // Focus the overlay when shown
      overlayRef.current.focus();
    }
  }, [show]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    } else if (e.key === "Tab") {
      // Trap focus within the dialog
      const focusableElements = overlayRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusableElements && focusableElements.length > 0) {
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[
          focusableElements.length - 1
        ] as HTMLElement;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    }
  };

  if (!show) return null;

  // Default button labels
  const defaultLabels: Partial<Record<MessageBoxButton, string>> = {
    ok: "OK",
    cancel: "Cancel",
    yes: "Yes",
    no: "No",
    agree: "Agree",
    disagree: "Disagree",
    retry: "Retry",
    close: "Close",
    upgrade: "Upgrade",
  };

  // Default icons for each type
  const defaultIcons: Record<MessageBoxType, string> = {
    error: "❌",
    warning: "⚠️",
    info: "ℹ️",
    success: "✅",
    confirm: "❓",
  };

  const displayIcon = icon || defaultIcons[type] || "";
  const labels = { ...defaultLabels, ...(buttonLabels || {}) };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closeOnBackdrop) {
      onClose();
    }
  };

  const handleButtonClick = (button: MessageBoxButton) => {
    onButtonClick(button);
  };

  return (
    <div
      ref={overlayRef}
      className="message-box-overlay"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="message-box-title"
      aria-describedby="message-box-message"
      tabIndex={-1}
    >
      <div className={`message-box message-box--${type}`}>
        {/* Header */}
        <div className="message-box__header">
          <div className="message-box__icon">
            <span className="message-box__icon-symbol">{displayIcon}</span>
          </div>
          <div className="message-box__title-section">
            <h3 id="message-box-title" className="message-box__title">
              {title}
            </h3>
            {description && (
              <p className="message-box__description">{description}</p>
            )}
          </div>
          {showCloseButton && (
            <button
              className="message-box__close"
              onClick={onClose}
              aria-label="Close dialog"
            >
              ✕
            </button>
          )}
        </div>

        {/* Content */}
        <div className="message-box__content">
          <p id="message-box-message" className="message-box__message">
            {message}
          </p>
        </div>

        {/* Actions */}
        <div className="message-box__actions">
          {buttons.map((button, index) => (
            <button
              key={button}
              ref={index === 0 ? firstButtonRef : undefined}
              className={`message-box__button message-box__button--${button} message-box__button--${type}`}
              onClick={() => handleButtonClick(button)}
              autoFocus={index === 0}
            >
              {labels[button] || button}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Convenience hook for easy usage.
// All returned functions are referentially stable (useCallback) and the
// returned messageBox object is memoised so downstream dependency arrays
// don't invalidate every render (was the #1 source of cascading re-render loops).
export const useMessageBox = () => {
  const [messageBox, setMessageBox] = React.useState<{
    show: boolean;
    type: MessageBoxType;
    title: string;
    message: string;
    description?: string;
    buttons: MessageBoxButton[];
    onButtonClick: (button: MessageBoxButton) => void;
    icon?: string;
    buttonLabels?: Partial<Record<MessageBoxButton, string>>;
  }>({
    show: false,
    type: "info",
    title: "",
    message: "",
    buttons: ["ok"],
    onButtonClick: () => {},
  });

  const onButtonClickRef = React.useRef<(button: MessageBoxButton) => void>(
    () => {},
  );
  onButtonClickRef.current = messageBox.onButtonClick;

  const showMessageBox = React.useCallback(
    (config: {
      type: MessageBoxType;
      title: string;
      message: string;
      description?: string;
      buttons: MessageBoxButton[];
      onButtonClick: (button: MessageBoxButton) => void;
      icon?: string;
      buttonLabels?: Partial<Record<MessageBoxButton, string>>;
    }) => {
      setMessageBox({
        show: true,
        ...config,
      });
    },
    [],
  );

  const hideMessageBox = React.useCallback(() => {
    setMessageBox((prev) => ({ ...prev, show: false }));
  }, []);

  const handleButtonClick = React.useCallback((button: MessageBoxButton) => {
    onButtonClickRef.current(button);
    setMessageBox((prev) => ({ ...prev, show: false }));
  }, []);

  const stableMessageBox = React.useMemo(
    () => ({
      ...messageBox,
      onButtonClick: handleButtonClick,
      onClose: hideMessageBox,
    }),
    [messageBox, handleButtonClick, hideMessageBox],
  );

  return React.useMemo(
    () => ({
      messageBox: stableMessageBox,
      showMessageBox,
      hideMessageBox,
    }),
    [stableMessageBox, showMessageBox, hideMessageBox],
  );
};

export default MessageBox;
